import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function useRealtimeNotifications() {
  const { user, token } = useAuth();
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (!user || !token) return;
    
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create SSE connection with auth
    const url = `${API_URL}/api/notifications/stream`;
    
    // Use fetch with EventSource polyfill pattern for auth header support
    const connectSSE = async () => {
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
          },
        });

        if (!response.ok) {
          throw new Error('SSE connection failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const processStream = async () => {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value);
            const lines = text.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  handleNotification(data);
                } catch (e) {
                  // Ignore parse errors for heartbeats
                }
              }
            }
          }
        };

        processStream().catch(() => {
          // Reconnect on stream end
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        });

      } catch (error) {
        console.error('SSE connection error:', error);
        // Retry connection after delay
        reconnectTimeoutRef.current = setTimeout(connect, 5000);
      }
    };

    connectSSE();
  }, [user, token]);

  const handleNotification = (data) => {
    if (data.type === 'heartbeat' || data.type === 'connected') {
      return;
    }

    // Show toast notification
    if (data.type === 'new_offer') {
      toast.success(data.title, {
        description: data.message,
        action: data.link ? {
          label: 'Voir',
          onClick: () => window.location.href = data.link,
        } : undefined,
        duration: 8000,
      });
    } else if (data.type === 'new_message') {
      toast(data.title, {
        description: data.message,
        action: data.link ? {
          label: 'Répondre',
          onClick: () => window.location.href = data.link,
        } : undefined,
        duration: 6000,
      });
    } else if (data.type === 'offer_accepted') {
      toast.success(data.title, {
        description: data.message,
        duration: 8000,
      });
    } else if (data.type === 'counter_offer') {
      toast.info(data.title, {
        description: data.message,
        action: data.link ? {
          label: 'Voir',
          onClick: () => window.location.href = data.link,
        } : undefined,
        duration: 8000,
      });
    }

    // Dispatch custom event for components to react
    window.dispatchEvent(new CustomEvent('realtime-notification', { detail: data }));
  };

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return null;
}

export default useRealtimeNotifications;
